import { Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { crudGuard, detailGuard } from '~ngx-shared/guards';
import { breadcrumbResolver, crudResolver } from '~ngx-shared/resolvers';
import { ApplicationListVariantEnum } from '~shared/models';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/layout.component').then(m => m.LayoutComponent),
    providers: [provideTranslocoScope('shared'), provideTranslocoScope('membership')],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      /******************
       * HOME
       ******************/
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard-page/dashboard-page.component').then(
            m => m.DashboardPageComponent
          ),
        data: {
          breadcrumb: 'dashboard'
        }
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile-page/profile-page.component').then(m => m.ProfilePageComponent),
        data: {
          breadcrumb: 'profile'
        }
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings-page/settings-page.component').then(
            m => m.SettingsPageComponent
          ),
        data: {
          breadcrumb: 'settings'
        }
      },
      /******************
       * ACTORS
       ******************/
      {
        path: 'actors',
        data: {
          breadcrumb: 'shared.actors'
        },
        children: [
          {
            path: 'people',
            data: {
              breadcrumb: 'shared.people'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/person-list-page/person-list-page.component').then(
                    m => m.PersonListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                loadComponent: () =>
                  import('./pages/person-detail-page/person-detail-page.component').then(
                    m => m.PersonDetailPageComponent
                  ),
                canActivate: [detailGuard('person-id')],
                resolve: { crud: crudResolver('person-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.person'
                }
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('~shared/core/pages/create-person-data-form-page/create-person-data-form-page.component').then(
                    m => m.CreatePersonDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.person'
                }
              }
            ]
          },
          {
            path: 'member-offices',
            data: {
              breadcrumb: 'shared.role.membership_offices',
              icon: 'pi pi-fw pi-briefcase',
              permissions: {
                only: RoleEnum.MODERATOR,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/member-office-list-page/member-office-list-page.component').then(
                    m => m.MemberOfficeListPageComponent
                  )
              },
              {
                path: 'detail/:member-office-id',
                redirectTo: '/actors/people/detail/:member-office-id'
              },
              {
                path: ':member-office-id',
                loadComponent: () =>
                  import('./pages/create-member-office-data-form-page/create-member-office-data-form-page.component').then(
                    m => m.CreateMemberOfficeDataFormPageComponent
                  ),
                canActivate: [crudGuard('member-office-id')],
                resolve: {
                  crud: crudResolver('member-office-id'),
                  breadcrumb: breadcrumbResolver('member-office-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.membership_office'
                }
              }
            ]
          },
          {
            path: 'membership-applications',
            data: {
              breadcrumb: 'membership.title',
              icon: 'pi pi-fw pi-inbox',
              permissions: {
                only: AuthorizationUtil.GROUP_MEMBERSHIP_ALL,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('~shared/shared/pages/application-list-page/application-list-page.component').then(
                    m => m.ApplicationListPageComponent
                  ),
                data: { variant: ApplicationListVariantEnum.MEMBERSHIP }
              },
              {
                path: 'detail/:id',
                loadComponent: () =>
                  import('./pages/membership-application-detail-page/membership-application-detail-page.component').then(
                    m => m.MembershipApplicationDetailPageComponent
                  ),
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'membership.title'
                }
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/create-update-membership-application-form-page/create-update-membership-application-form-page.component').then(
                    m => m.CreateUpdateMembershipApplicationFormPageComponent
                  ),
                canActivate: [crudGuard('id')],
                resolve: {
                  crud: crudResolver('id'),
                  breadcrumb: breadcrumbResolver('id')
                },
                data: {
                  breadcrumbParamValue: 'membership.title'
                }
              }
            ]
          },
          {
            path: 'military-applications',
            data: {
              breadcrumb: 'membership.military_title',
              icon: 'pi pi-fw pi-shield',
              permissions: {
                only: AuthorizationUtil.GROUP_MEMBERSHIP_ALL,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('~shared/shared/pages/application-list-page/application-list-page.component').then(
                    m => m.ApplicationListPageComponent
                  ),
                data: { variant: ApplicationListVariantEnum.MILITARY }
              },
              {
                path: 'detail/:id',
                loadComponent: () =>
                  import('./pages/military-application-detail-page/military-application-detail-page.component').then(
                    m => m.MilitaryApplicationDetailPageComponent
                  ),
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'membership.military_title'
                }
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/create-update-military-application-form-page/create-update-military-application-form-page.component').then(
                    m => m.CreateUpdateMilitaryApplicationFormPageComponent
                  ),
                canActivate: [crudGuard('id')],
                resolve: {
                  crud: crudResolver('id'),
                  breadcrumb: breadcrumbResolver('id')
                },
                data: {
                  breadcrumbParamValue: 'membership.military_title'
                }
              }
            ]
          },
          {
            path: 'conversion-applications',
            data: {
              breadcrumb: 'membership.conversion_title',
              icon: 'pi pi-fw pi-sync',
              permissions: {
                only: AuthorizationUtil.GROUP_MEMBERSHIP_ALL,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('~shared/shared/pages/application-list-page/application-list-page.component').then(
                    m => m.ApplicationListPageComponent
                  ),
                data: { variant: ApplicationListVariantEnum.CONVERSION }
              },
              {
                path: 'detail/:id',
                loadComponent: () =>
                  import('./pages/conversion-application-detail-page/conversion-application-detail-page.component').then(
                    m => m.ConversionApplicationDetailPageComponent
                  ),
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'membership.conversion_title'
                }
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/create-update-conversion-application-form-page/create-update-conversion-application-form-page.component').then(
                    m => m.CreateUpdateConversionApplicationFormPageComponent
                  ),
                canActivate: [crudGuard('id')],
                resolve: {
                  crud: crudResolver('id'),
                  breadcrumb: breadcrumbResolver('id')
                },
                data: {
                  breadcrumbParamValue: 'membership.conversion_title'
                }
              }
            ]
          },
          {
            path: 'school-enrolment-applications',
            data: {
              breadcrumb: 'shared.school_enrolment_title',
              icon: 'pi pi-fw pi-book',
              permissions: {
                only: AuthorizationUtil.GROUP_MEMBERSHIP_ALL,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('~shared/shared/pages/application-list-page/application-list-page.component').then(
                    m => m.ApplicationListPageComponent
                  ),
                data: { variant: ApplicationListVariantEnum.SCHOOL_ENROLMENT }
              },
              {
                path: 'detail/:id',
                loadComponent: () =>
                  import('~shared/shared/pages/school-enrolment-application-detail-page/school-enrolment-application-detail-page.component').then(
                    m => m.SchoolEnrolmentApplicationDetailPageComponent
                  ),
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.school_enrolment_title'
                }
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/create-update-school-enrolment-application-form-page/create-update-school-enrolment-application-form-page.component').then(
                    m => m.CreateUpdateSchoolEnrolmentApplicationFormPageComponent
                  ),
                canActivate: [crudGuard('id')],
                resolve: {
                  crud: crudResolver('id'),
                  breadcrumb: breadcrumbResolver('id')
                },
                data: {
                  breadcrumbParamValue: 'shared.school_enrolment_title'
                }
              }
            ]
          },
          {
            path: 'members',
            data: {
              breadcrumb: 'shared.role.members'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/member-list-page/member-list-page.component').then(
                    m => m.MemberListPageComponent
                  )
              },
              {
                path: 'detail/:member-id',
                redirectTo: '/actors/people/detail/:member-id'
              },
              {
                path: ':member-id',
                loadComponent: () =>
                  import('./pages/create-member-data-form-page/create-member-data-form-page.component').then(
                    m => m.CreateMemberDataFormPageComponent
                  ),
                canActivate: [crudGuard('member-id')],
                resolve: {
                  crud: crudResolver('member-id'),
                  breadcrumb: breadcrumbResolver('member-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.member'
                }
              }
            ]
          }
        ]
      },
      /******************
       * ADMINISTRATION
       ******************/
      {
        path: 'administration',
        data: {
          breadcrumb: 'shared.administration'
        },
        children: [
          {
            path: 'actors',
            data: {
              breadcrumb: 'shared.actors'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/actor-administration-page/actor-administration-page.component').then(
                    m => m.ActorAdministrationPageComponent
                  )
              },
              {
                path: 'member-status',
                data: {
                  breadcrumb: 'shared.member_status'
                },
                children: [
                  {
                    path: ':member-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-member-status-form-page/create-update-member-status-form-page.component').then(
                        m => m.CreateUpdateMemberStatusFormPageComponent
                      ),
                    canActivate: [crudGuard('member-status-id')],
                    resolve: {
                      crud: crudResolver('member-status-id'),
                      breadcrumb: breadcrumbResolver('member-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'shared.member_status'
                    }
                  }
                ]
              },
              {
                path: 'membership-application-status',
                data: {
                  breadcrumb: 'shared.membership_application_status'
                },
                children: [
                  {
                    path: ':membership-application-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-membership-application-status-form-page/create-update-membership-application-status-form-page.component').then(
                        m => m.CreateUpdateMembershipApplicationStatusFormPageComponent
                      ),
                    canActivate: [crudGuard('membership-application-status-id')],
                    resolve: {
                      crud: crudResolver('membership-application-status-id'),
                      breadcrumb: breadcrumbResolver('membership-application-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'shared.membership_application_status'
                    }
                  }
                ]
              }
            ]
          }
        ]
      },
      /******************
       * MAIL TEMPLATES
       ******************/
      {
        path: 'mail-templates',
        data: {
          breadcrumb: 'shared.mail_templates',
          permissions: {
            only: AuthorizationUtil.GROUP_MEMBERSHIP_ALL,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('~shared/mail/pages/mail-template-list-page/mail-template-list-page.component').then(
                m => m.MailTemplateListPageComponent
              )
          },
          {
            path: 'detail/:mail-template-id',
            loadComponent: () =>
              import('~shared/mail/pages/mail-template-detail-page/mail-template-detail-page.component').then(
                m => m.MailTemplateDetailPageComponent
              ),
            canActivate: [detailGuard('mail-template-id')],
            resolve: { crud: crudResolver('mail-template-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.mail_template'
            }
          },
          {
            path: 'detail/data/:mail-template-data-id',
            loadComponent: () =>
              import('~shared/mail/pages/mail-template-detail-page/mail-template-detail-page.component').then(
                m => m.MailTemplateDetailPageComponent
              ),
            canActivate: [detailGuard('mail-template-data-id')],
            resolve: { crud: crudResolver('mail-template-data-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.mail_template',
              isMailTemplateData: true
            }
          },
          {
            path: ':mail-template-id',
            loadComponent: () =>
              import('~shared/mail/pages/create-update-mail-template-form-page/create-update-mail-template-form-page.component').then(
                m => m.CreateUpdateMailTemplateFormPageComponent
              ),
            canActivate: [crudGuard('mail-template-id')],
            resolve: {
              crud: crudResolver('mail-template-id'),
              breadcrumb: breadcrumbResolver('mail-template-id')
            },
            data: {
              breadcrumbParamValue: 'shared.mail_template'
            }
          }
        ]
      },
      /******************
       * PRINT TEMPLATES
       ******************/
      {
        path: 'print-templates',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.print_templates',
          permissions: {
            only: AuthorizationUtil.GROUP_MEMBERSHIP_ALL,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('~shared/print/pages/print-management-page/print-management-page.component').then(
                m => m.PrintManagementPageComponent
              )
          },
          {
            path: ':print-template-id',
            loadComponent: () =>
              import('~shared/print/pages/create-update-print-template-form-page/create-update-print-template-form-page.component').then(
                m => m.CreateUpdatePrintTemplateFormPageComponent
              ),
            canActivate: [crudGuard('print-template-id')],
            resolve: {
              crud: crudResolver('print-template-id'),
              breadcrumb: breadcrumbResolver('print-template-id')
            },
            data: {
              breadcrumbParamValue: 'shared.print_template'
            }
          }
        ]
      },
      /******************
       * NOT FOUND
       ******************/
      {
        path: 'not-found',
        loadComponent: () =>
          import('~shared/shared/pages/page-not-found-page/page-not-found-page.component').then(
            m => m.PageNotFoundPageComponent
          ),
        data: {
          breadcrumb: 'page_not_found'
        }
      },
      { path: '**', redirectTo: 'not-found' }
    ]
  }
];
